import {DateTime} from "luxon";

/**
 * Filters object keys
 * example:
 * var object = {a:1, b:2, c:3}
 * filterObjectKeys(object, ['b', 'a']) => {a: 1, b:2}
 *
 * if exclude mode is true, then it works vice versa leaving the keys which are not in the keys array
 * filterObjectKeys(object, ['a', 'b'], true) => { c:3 }
 * @param object
 * @param keys
 * @param excludeMode
 */
export const filterObjectKeys = <T extends Record<string | number, any>>(object: T, keys: any[], excludeMode = false): T => {
    const objectCopy = {...object};

    return Object.keys(objectCopy)
        .filter(key => excludeMode ? !keys.includes(key) : keys.includes(key))
        .reduce((obj: any, key: string) => {
            obj[key] = objectCopy[key];
            return obj;
        }, {});
}

/**
 * Map object keys
 * example:
 * var object = {a:1, b:2, c:3}
 * filterObjectKeys(object, ['b', 'a']) => {b: 2, a:1}
 *
 * @param object
 * @param keys
 */
export const mapObjectKeys = <T extends Record<string | number, any>>(object: T, keys: any[]): T => {
    const objectCopy = { ...object };

    return keys
        .reduce((obj: any, key: string) => {
            obj[key] = objectCopy[key];
            return obj;
        }, {});
}

/**
 * Adds a property to an object immutably
 * @param object
 * @param propertyKey
 * @param propertyValue
 */
export const addImmutably = <T extends Record<string | number, any>>(object: T, propertyKey: string, propertyValue: any): T => {
    return {...object, ...{[propertyKey]: propertyValue}};
}

/**
 * immutably delete
 * @param object
 * @param propertyKey
 */
export const removeImmutably = (object: Record<string | number, any>, propertyKey: string) => {
    if (object && typeof object == 'object') {
        const {[propertyKey]: deletedItems, ...itemsWithoutDeleted} = object;
        return itemsWithoutDeleted;
    }

    return object;
}

/**
 * Return values by list of the keys, access is case-insensitive
 * i.e. obj = {a: {b: 1}}
 * getIgnoreCase(obj, ['a','b'], 0) => 1
 * getIgnoreCase(obj, ['A','b'], 0) => 1
 * getIgnoreCase(obj, ['a','B'], 0) => 1
 * getIgnoreCase(obj, ['A','B'], 0) => 1
 * getIgnoreCase(obj, ['a1','b'], 0) => 0
 * @param object
 * @param keys
 * @param defaultValue
 */
export const getIgnoreCase = <T>(object: Record<string | number, any>, keys: string | string[], defaultValue: T): T => {
    if (!Array.isArray(keys)) {
        keys = [keys];
    }

    let current = object;

    for (const key of keys) {
        if (typeof current != 'object') { return defaultValue; }

        const lowerCaseKey = key.toLowerCase();

        const currentKey = current && Object.keys(current).find(item => item.toLowerCase() == lowerCaseKey);

        if (currentKey) {
            current = current[currentKey];
            continue;
        }

        return defaultValue;
    }

    return current as T;
}
/**
 * Set object value (even nested values)
 * setObjectValue({a:1}, ['set','value'], 'value') => {a:1, set:{ value: 'value' }}
 * @param object
 * @param path
 * @param value
 */
export const setObjectValue = (object: Record<any, any>, path: string[], value: any) => {
    if (typeof object != 'object') {return object;}

    const lastIndex = path.length - 1;
    let current = object;
    for(let i = 0; i <= lastIndex; i++) {
        const pathName = path[i];

        if (i == lastIndex) {
            current[pathName] = value;
        }
        else {
            if (!Object.prototype.hasOwnProperty.call(current, pathName) || typeof current[pathName] !== "object") {
                current[pathName] = {}
            }
            current = current[pathName]
        }
    }

    return object;
}


export const deepCopy = <T>(source: T): T => {
    if (Array.isArray(source)) {
        return source.map(item => deepCopy(item)) as any;
    }
    if (source instanceof Date) {
        return new Date(source.getTime()) as any;
    }

    if (source instanceof DateTime) {
        return source;
    }

    if (source && typeof source === 'object') {
        return Object.keys(source).reduce((o, prop) => {
            o[prop] = deepCopy((source as { [key: string]: any })[prop]);
            return o;
        }, {} as any) as T; //, Object.create(Object.getPrototypeOf(source))
    }

    return source as T;
}
